import { useState } from 'react';
import { useHistory, type HistoryParams } from '../hooks/useApi';

const CATEGORY_COLORS: Record<string, string> = {
  CUSTOM_ORDER: 'bg-blue-100 text-blue-800',
  PROD_STATUS: 'bg-green-100 text-green-800',
  SHIPPING: 'bg-yellow-100 text-yellow-800',
  PRODUCT_SUPPORT: 'bg-teal-100 text-teal-800',
  WHOLESALE: 'bg-purple-100 text-purple-800',
  FEEDBACK: 'bg-orange-100 text-orange-800',
  BOOKING: 'bg-cyan-100 text-cyan-800',
  INTERNAL: 'bg-gray-100 text-gray-700',
};

const CATEGORIES = Object.keys(CATEGORY_COLORS);

const ACTION_DISPLAY: Record<string, { icon: string; label: string; className: string }> = {
  sent: { icon: '\u2713', label: 'Sent', className: 'text-emerald-600' },
  auto: { icon: '\u2699', label: 'Auto', className: 'text-blue-600' },
  edited: { icon: '\u270E', label: 'Edited', className: 'text-amber-600' },
  rejected: { icon: '\u2717', label: 'Rejected', className: 'text-red-600' },
  escalated: { icon: '\u26A0', label: 'Escalated', className: 'text-red-600' },
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function HistoryView() {
  const [params, setParams] = useState<HistoryParams>({ page: 1, per_page: 25 });
  const [searchInput, setSearchInput] = useState('');
  const { data, isLoading, isError } = useHistory(params);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (params.per_page ?? 25)));
  const page = data?.page ?? 1;

  const applySearch = () => {
    setParams((p) => ({ ...p, search: searchInput || undefined, page: 1 }));
  };

  return (
    <div>
      {/* Filters bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex">
          <input
            type="text"
            placeholder="Search emails..."
            className="input rounded-r-none w-64"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          />
          <button onClick={applySearch} className="btn-primary rounded-l-none text-xs">
            Search
          </button>
        </div>

        <select
          className="select w-44"
          value={params.category ?? ''}
          onChange={(e) =>
            setParams((p) => ({ ...p, category: e.target.value || undefined, page: 1 }))
          }
        >
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c.replace('_', ' ')}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="input w-40"
          value={params.date_from ?? ''}
          onChange={(e) =>
            setParams((p) => ({ ...p, date_from: e.target.value || undefined, page: 1 }))
          }
        />
        <span className="text-gray-400 text-sm">to</span>
        <input
          type="date"
          className="input w-40"
          value={params.date_to ?? ''}
          onChange={(e) =>
            setParams((p) => ({ ...p, date_to: e.target.value || undefined, page: 1 }))
          }
        />

        <span className="ml-auto text-sm text-gray-500">{total} total</span>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-center">Confidence</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-red-500">
                    Failed to load history.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    No history found.
                  </td>
                </tr>
              )}
              {items.map((item) => {
                const actionCfg = ACTION_DISPLAY[item.action] ?? ACTION_DISPLAY.sent;
                const catClass = CATEGORY_COLORS[item.category] ?? 'bg-gray-100 text-gray-700';
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">
                      {formatDate(item.processed_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]">
                        {item.from_name || item.from_email}
                      </div>
                      {item.from_name && (
                        <div className="text-xs text-gray-400 truncate max-w-[200px]">{item.from_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 truncate max-w-[300px]">{item.subject}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${catClass}`}>{item.category.replace('_', ' ')}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs font-medium">
                      <span
                        className={
                          item.confidence >= 0.9
                            ? 'text-emerald-600'
                            : item.confidence >= 0.5
                              ? 'text-amber-600'
                              : 'text-red-600'
                        }
                      >
                        {(item.confidence * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${actionCfg.className}`}>
                        <span>{actionCfg.icon}</span>
                        {actionCfg.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              className="btn-outline text-xs"
              disabled={page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
            >
              Previous
            </button>
            <button
              className="btn-outline text-xs"
              disabled={page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
